import { useEffect, useSyncExternalStore } from 'react';
import { fetchPlaceStats } from '../services/reviewsService';

// Shared hook for the live Google Place aggregate (total rating count +
// overall rating). Wraps `fetchPlaceStats` from reviewsService so consumers
// don't have to know about the underlying backend `/v1/website/reviews` call.
//
// Module-level store so every consumer on the same page load (the floating
// GoogleReviewsBadge widget, TrustBridge, SocialProofSection,
// RecentInstallsSection, /reviews, /shingles) shares a SINGLE network fetch.
//
// ── HYDRATION SAFETY (do not "simplify" this back to useState) ────────────
// The public marketing routes are prerendered at build time
// (scripts/prerender.mjs) and hydrated with React 18 hydrateRoot. The
// prerender has no live data, so the server HTML always carries each
// consumer's FALLBACK_* constant (e.g. TrustBridge's 32 verified reviews).
//
// This hook previously seeded state straight from the module cache
// (`useState(cachedStats)`), which read mutable module state DURING render.
// React 18 hydrates the <Suspense> boundary in Routes.jsx in a LOWER-PRIORITY
// pass than the surrounding shell, so on a real page load the ordering was:
//
//   1. the shell (App's GoogleReviewsBadge/StickyCallTab/MobileEstimateFAB)
//      hydrates and commits;
//   2. its effects run -> fetchPlaceStats() fires and resolves (~25 ms on
//      prod, where the endpoint is warm);
//   3. the module cache is now populated, and only THEN does React hydrate
//      the boundary's contents -> TrustBridge's first render produced the
//      live "34" against server HTML that said "32".
//
// That is a guaranteed text-content mismatch: React error #425 plus a cascade
// of #418s and a #422 ("error while hydrating this Suspense boundary"),
// 16 console errors on every prod homepage load. It only ever reproduced when
// the fetch SUCCEEDED, which is why it was permanent on prod, intermittent on
// dev, and invisible locally (CORS makes the call fail).
//
// useSyncExternalStore is the primitive React provides for exactly this:
// React itself calls getServerSnapshot() while a tree is hydrating and
// getSnapshot() afterwards. So the hydrating render always matches the
// prerendered HTML, live data lands in a post-hydration re-render, and a
// component mounted later by client-side navigation still reads the warm
// cache synchronously (no fallback flash).
//
// Returns:
//   stats     - { totalReviews, overallRating, updatedAt } | null
//   loading   - true until the first fetch settles
//   error     - Error from fetchPlaceStats, or null

// The pre-fetch state. Frozen and reused as the server/hydration snapshot so
// the reference is stable across renders (an unstable snapshot would make
// useSyncExternalStore re-render forever).
const EMPTY = Object.freeze({ stats: null, loading: true, error: null });

let snapshot = EMPTY;
let started = false;
const listeners = new Set();

const emit = () => {
  for (const listener of [...listeners]) listener();
};

const subscribe = (onStoreChange) => {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
};

const getSnapshot = () => snapshot;

// What the build-time prerender rendered, and therefore what the first
// (hydrating) client render MUST render: no live data yet.
const getServerSnapshot = () => EMPTY;

// One fetch per page load, shared by every consumer. Kicked off from an
// effect, so it never runs during react-dom/server rendering.
const loadOnce = () => {
  if (started) return;
  started = true;

  Promise.resolve(fetchPlaceStats())
    .then((data) => {
      // fetchPlaceStats already swallows its own errors and resolves null.
      snapshot = data
        ? { stats: data, loading: false, error: null }
        : { ...snapshot, loading: false };
    })
    .catch((err) => {
      snapshot = { stats: snapshot.stats, loading: false, error: err };
    })
    .finally(emit);
};

export const useGoogleStats = () => {
  const { stats, loading, error } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  useEffect(loadOnce, []);

  return { stats, loading, error };
};
